import React from "react";
import * as Mui from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { RequestStatusChip, requestStatusColor } from "./RequestStatusChip";

// Single-line icon+value info item (no stacked field label) with an
// optional vertical divider bar, so the row reads as one inline strip —
// e.g. "STR0013 | Alpha | Developer" — instead of a label/value grid.
const HeaderInfoItem = ({ icon: Icon, value, withDivider = false }) => (
  <Mui.Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      minWidth: { xs: "100%", sm: 0 },
      flex: { xs: "1 1 100%", sm: "0 1 auto" },
      pl: withDivider ? 2 : 0,
      ml: withDivider ? 0.25 : 0,
      borderLeft: withDivider ? `1px solid ${designColors.slate200}` : "none",
    }}
  >
    {Icon ? (
      <Icon sx={{ fontSize: 17, color: designColors.slate500, flexShrink: 0 }} />
    ) : null}
    <Mui.Typography
      title={value ?? "-"}
      sx={{
        color: designColors.slate800,
        fontFamily: designFontFamily,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.25,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value ?? "-"}
    </Mui.Typography>
  </Mui.Stack>
);

// Header block for the redesigned "view request" side panels: an avatar with
// a small status-colored dot badge, the requester's name + a status chip,
// then an icon-led row of Emp ID / Department / Designation.
//
// Deliberately a standalone component rather than an extension of
// UserDetailsRow (CustomComponents/UserDetailsRow.js) — that component is
// shared by 9+ apply/create panels with a different (two-row label/value
// grid) layout, and this mockup's header is a different enough shape that
// bolting it on would mean threading new optional props through every one
// of those unrelated callers just to support this one screen family.
export const RequestDetailHeader = ({
  userDetails,
  status,
  colorCode,
}) => {
  // Apply/create panels reuse this same header to show the picked employee
  // before a request (and its status) exists — skip the status dot + chip
  // entirely in that case instead of showing a meaningless "-" badge.
  const hasStatus = Boolean(status);
  const badgeColor = requestStatusColor(status, colorCode);

  const avatar = (
    <Mui.Avatar
      src={userDetails?.image_url}
      alt={userDetails?.user_name}
      sx={{ width: 56, height: 56 }}
    />
  );

  return (
    <Mui.Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designColors.slate200}`,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${designColors.tealTint} 0%, ${designColors.white} 100%)`,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        fontFamily: designFontFamily,
      }}
    >
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        {hasStatus ? (
          <Mui.Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Mui.Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: badgeColor,
                  border: `3px solid ${designColors.white}`,
                }}
              />
            }
          >
            {avatar}
          </Mui.Badge>
        ) : (
          avatar
        )}

        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            sx={{ rowGap: 0.75 }}
          >
            <Mui.Typography
              sx={{
                fontSize: { xs: 17, sm: 18 },
                fontWeight: 700,
                color: designColors.slate900,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userDetails?.user_name ?? "-"}
            </Mui.Typography>
            {hasStatus ? (
              <RequestStatusChip status={status} colorCode={colorCode} />
            ) : null}
          </Mui.Stack>

          <Mui.Stack
            direction="row"
            spacing={0}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mt: 1.4, rowGap: 1 }}
          >
            <HeaderInfoItem value={userDetails?.employee_id} />
            <HeaderInfoItem
              icon={ApartmentOutlinedIcon}
              value={userDetails?.department_name}
              withDivider
            />
            <HeaderInfoItem
              icon={WorkOutlineIcon}
              value={userDetails?.designation}
              withDivider
            />
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  );
};

export default RequestDetailHeader;
